import {
  type CampaignAnalyticsGranularity,
  type DashboardRange,
  isPrivilegedRole,
  type PortfolioDetailResponse,
  type PortfolioMemberCampaign,
  type PortfolioState,
} from "@adpilot/shared";
import { IconDatabaseOff } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { type CSSProperties, useState } from "react";
import { useTranslation } from "react-i18next";
import { CampaignColumnResizeHandle } from "@/components/campaign/CampaignColumnResizeHandle";
import {
  clampCampaignTableColumnWidth,
  getCampaignTableDefaultWidths,
} from "@/components/campaign/campaign-table-layout";
import {
  DataGridBody,
  DataGridHeader,
  dataGridRowClassName,
} from "@/components/patterns/DataTable";
import { ReportingFilters } from "@/components/patterns/reporting-filters";
import { PortfolioCampaignsMenu } from "@/components/portfolio/PortfolioCampaignsMenu";
import { PortfolioPerformancePanel } from "@/components/portfolio/PortfolioPerformancePanel";
import { MetricHeaderCells, MetricValueCells } from "@/components/portfolio/portfolio-metrics";
import { BudgetHealthBadge } from "@/components/shared/BudgetHealthBadge";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import type { PerformanceMetric } from "@/components/shared/performance/performance";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useFormatters } from "@/lib/format";
import { requireModule } from "@/lib/module-access";
import { fetchPortfolioDetail } from "@/lib/portfolio-api";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";
import { reportingPeriodSearchParams, useReportingPeriodStore } from "@/stores/reporting-period";
import { useReportingTimeZoneStore } from "@/stores/reporting-time-zone";

export const Route = createFileRoute("/_authed/portfolios_/$id")({
  beforeLoad: () => {
    requireModule("campaigns");
  },
  component: PortfolioDetailPage,
});

// name / status / budget / start / end, then the 12 shared metric columns.
const MEMBER_GRID =
  // pl-0 drops the grid's left padding so the sticky name column rests exactly
  // where `sticky left-0` pins it (no 14px drift on horizontal scroll); the
  // name cell re-adds its own pl-3.5 so text inset is unchanged.
  "grid-cols-[var(--member-name-width)_84px_92px_100px_100px_repeat(12,minmax(66px,1fr))] gap-x-4 pl-0";

function PortfolioDetailPage() {
  const { t } = useTranslation("portfolios");
  const { id } = Route.useParams();
  const range = useReportingPeriodStore((state) => state.period);
  const timeZone = useReportingTimeZoneStore((state) => state.timeZone);
  const currentShopId = useAuthStore((state) => state.currentShopId);
  const user = useAuthStore((state) => state.user);
  const role = user?.workspaces?.find(
    (workspace) => workspace.id === user?.activeWorkspaceId,
  )?.role;
  const canWrite = isPrivilegedRole(role);
  const [metric, setMetric] = useState<PerformanceMetric>("spend");
  const [granularity, setGranularity] = useState<CampaignAnalyticsGranularity>("day");

  const query = useQuery({
    queryKey: ["portfolios", "detail", currentShopId, id, range, timeZone, granularity],
    queryFn: () => fetchPortfolioDetail(id, range, granularity, timeZone),
  });

  if (query.isLoading) {
    return (
      <PortfolioDetailFrame>
        <PortfolioDetailSkeleton />
      </PortfolioDetailFrame>
    );
  }

  if (query.isError || !query.data) {
    return (
      <PortfolioDetailFrame>
        <ErrorState message={t("loadFailed")} onRetry={() => void query.refetch()} />
      </PortfolioDetailFrame>
    );
  }

  const { portfolio, shopId, kpis, trend, dataThrough, memberCampaigns } = query.data;
  const eligibleCampaignRange = reportingPeriodSearchParams(range, timeZone).range;

  return (
    <PortfolioDetailFrame>
      <PortfolioDetailHeader portfolio={portfolio} />
      <PortfolioPerformancePanel
        shopIds={[shopId]}
        kpis={kpis}
        trend={trend}
        dataThrough={dataThrough}
        metric={metric}
        granularity={granularity}
        onMetricChange={setMetric}
        onGranularityChange={setGranularity}
        isLoading={false}
        isFetching={query.isFetching}
        isError={query.isError}
        onRetry={() => void query.refetch()}
      />
      <MembersSection
        members={memberCampaigns}
        portfolioId={id}
        shopId={shopId}
        range={eligibleCampaignRange}
        canWrite={canWrite}
      />
    </PortfolioDetailFrame>
  );
}

function PortfolioDetailFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-full w-full min-w-0 flex-1 flex-col gap-[14px] px-6 pb-[22px] pt-[22px]">
      {children}
    </div>
  );
}

function PortfolioDetailHeader({ portfolio }: { portfolio: PortfolioDetailResponse["portfolio"] }) {
  const { t } = useTranslation("portfolios");
  return (
    <div className="flex shrink-0 flex-wrap items-start justify-between gap-x-6 gap-y-3">
      <div className="min-w-0 flex-1 basis-[180px]">
        <div className="mb-1.5 flex items-center gap-1 text-[10px] text-muted-foreground">
          <Link to="/portfolios" className="hover:text-ink">
            {t("detail.breadcrumb")}
          </Link>
          <span>/</span>
          <span className="truncate">{portfolio.name}</span>
        </div>
        <div className="flex items-center gap-2">
          <h1 className="truncate text-[24px] font-semibold leading-7 tracking-[-0.02em] text-ink">
            {portfolio.name}
          </h1>
          <StateBadge state={portfolio.state} />
          <BudgetHealthBadge health={portfolio.budgetHealth} />
        </div>
      </div>
      <ReportingFilters scope="resource" />
    </div>
  );
}

function StateBadge({ state }: { state: PortfolioState }) {
  const { t } = useTranslation("portfolios");
  return (
    <Badge
      variant="outline"
      className={cn(
        state === "enabled" && "border-success/30 text-success-foreground",
        state === "paused" && "border-warning/30 text-warning-foreground",
        state === "archived" && "border-muted-foreground/30 text-muted-foreground",
      )}
    >
      {t(`state.${state}`)}
    </Badge>
  );
}

interface MembersSectionProps {
  members: PortfolioMemberCampaign[];
  portfolioId: string;
  shopId: string;
  range: DashboardRange;
  canWrite: boolean;
}

function MembersSection({ members, portfolioId, shopId, range, canWrite }: MembersSectionProps) {
  const { t } = useTranslation("portfolios");
  const [nameWidth, setNameWidth] = useState(getCampaignTableDefaultWidths().campaign);
  const tableStyle = {
    "--member-name-width": `${nameWidth}px`,
    minWidth: nameWidth + 1550,
  } as CSSProperties;

  return (
    <section className="flex min-h-[480px] flex-1 flex-col overflow-hidden rounded-[14px] border border-line bg-surface">
      <div className="flex h-[48px] shrink-0 items-center justify-between border-b border-line px-3.5">
        <h2 className="text-[14px] font-semibold text-ink">{t("detail.membersTitle")}</h2>
        <span className="text-[10px] text-muted-foreground">
          {t("detail.membersCount", { count: members.length })}
        </span>
      </div>
      {/* Xnurta parity (ADP-137): the campaign actions toolbar sits top-left
          above the table; write-gated, so members never see it. */}
      {canWrite ? (
        <div className="flex shrink-0 items-center border-b border-line px-3.5 py-2">
          <PortfolioCampaignsMenu portfolioId={portfolioId} shopId={shopId} range={range} />
        </div>
      ) : null}
      {members.length === 0 ? (
        <EmptyState
          icon={<IconDatabaseOff className="size-14" />}
          title={t("detail.emptyMembersTitle")}
          description={t("detail.emptyMembersDescription")}
        />
      ) : (
        <div className="min-h-0 flex-1 overflow-auto paper-scrollbar">
          <div style={tableStyle}>
            <DataGridHeader className={cn(MEMBER_GRID, "sticky top-0 z-20 bg-subtle")}>
              <span className="sticky left-0 z-30 flex items-center self-stretch border-r border-line bg-subtle pl-3.5 pr-3">
                {t("memberCampaigns.name")}
                <CampaignColumnResizeHandle
                  columnId="campaign"
                  width={nameWidth}
                  ariaLabel={t("campaign:list.table.resizeColumnAria", {
                    column: t("memberCampaigns.name"),
                  })}
                  onResize={(columnId, width) =>
                    setNameWidth(clampCampaignTableColumnWidth(columnId, width))
                  }
                  onReset={() => setNameWidth(getCampaignTableDefaultWidths().campaign)}
                />
              </span>
              <span>{t("memberCampaigns.status")}</span>
              <span className="text-right">{t("memberCampaigns.budget")}</span>
              <span>{t("memberCampaigns.startDate")}</span>
              <span>{t("memberCampaigns.endDate")}</span>
              <MetricHeaderCells />
            </DataGridHeader>
            <DataGridBody>
              {members.map((member) => (
                <MemberRow key={member.id} member={member} />
              ))}
            </DataGridBody>
          </div>
        </div>
      )}
    </section>
  );
}

function MemberRow({ member }: { member: PortfolioMemberCampaign }) {
  const { t } = useTranslation("portfolios");
  const { formatCurrency, formatDate } = useFormatters();
  return (
    <Link
      to="/campaigns/$id"
      params={{ id: member.id }}
      className={dataGridRowClassName("standard", cn(MEMBER_GRID, "bg-surface hover:bg-subtle"))}
    >
      {/* Sticky column stays opaque AND full-height (self-stretch) so a taller
          chip behind it can't peek out top & bottom; bg-inherit mirrors the
          row's own opaque background (surface at rest, subtle on hover) so it's
          never a white patch and never lets a chip bleed through. */}
      <span className="sticky left-0 z-10 flex min-w-0 flex-col justify-center self-stretch border-r border-line bg-inherit pl-3.5 pr-3">
        <span
          title={member.name}
          className="block truncate text-[11px] font-semibold text-ink hover:underline"
        >
          {member.name}
        </span>
        {member.targetingType ? (
          <span className="block truncate text-[9px] text-muted-foreground">
            {t(`memberCampaigns.targeting.${member.targetingType}`)}
          </span>
        ) : null}
      </span>
      <span className="flex items-center gap-1.5 capitalize text-muted-foreground">
        <span
          className={cn(
            "size-1.5 rounded-full",
            member.status === "enabled"
              ? "bg-success"
              : member.status === "paused"
                ? "bg-warning"
                : "bg-muted-foreground/50",
          )}
        />
        {t(`state.${member.status}`)}
      </span>
      <span className="text-right font-mono tabular-nums text-ink">
        {member.dailyBudget == null ? "—" : formatCurrency(member.dailyBudget)}
      </span>
      <span className="font-mono tabular-nums text-muted-foreground">
        {member.startDate == null ? "—" : formatDate(member.startDate)}
      </span>
      <span className="font-mono tabular-nums text-muted-foreground">
        {member.endDate == null ? "—" : formatDate(member.endDate)}
      </span>
      <MetricValueCells metrics={member.metrics} />
    </Link>
  );
}

function PortfolioDetailSkeleton() {
  return (
    <div className="space-y-[14px]">
      <div className="space-y-2">
        <Skeleton className="h-3 w-40" />
        <Skeleton className="h-8 w-72" />
      </div>
      <Skeleton className="h-[440px] w-full rounded-[14px]" />
      <Skeleton className="h-[280px] w-full rounded-[14px]" />
    </div>
  );
}
